import { useEffect, useSyncExternalStore } from 'react';
import { BUILD } from '@/config/build';
import { readPreference, writePreference } from './preferences';

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** A value React components follow; changes re-render them. */
function signal<T>(initial: T) {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next: T) {
      value = next;
      listeners.forEach((listener) => listener());
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
}

const useSignal = <T>(source: ReturnType<typeof signal<T>>): T => useSyncExternalStore(source.subscribe, source.get, source.get);

/**
 * An installed app stays open for days; the browser itself only looks for a new
 * version when a page is opened. Checking hourly (while visible) and whenever
 * the app comes back to the front keeps long-running sessions current.
 */
const UPDATE_CHECK_MS = 60 * 60 * 1000;

/** Set once a new version is downloaded and waiting; calling it reloads into it. */
const pendingUpdate = signal<(() => Promise<void>) | null>(null);

/**
 * Where the app stands with updates, for the version line at the foot of the
 * home page. `unsupported` in development and browsers without service workers.
 */
export type UpdateStatus = 'unsupported' | 'unchecked' | 'checking' | 'current' | 'ready' | 'offline';

const updateStatus = signal<UpdateStatus>('unsupported');
let registration: ServiceWorkerRegistration | null = null;

/** A new version is downloaded in the background; this long is the most a check waits for it. */
const INSTALL_WAIT_MS = 30_000;

function installed(worker: ServiceWorker | null): Promise<void> {
  if (!worker || worker.state !== 'installing') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      if (worker.state === 'installing') return;
      worker.removeEventListener('statechange', done);
      resolve();
    };
    worker.addEventListener('statechange', done);
    window.setTimeout(resolve, INSTALL_WAIT_MS);
  });
}

/** Asks the server for a newer version now, and settles on what it found. */
export async function checkForUpdate(): Promise<void> {
  if (!registration || pendingUpdate.get()) return;
  if (!navigator.onLine) return updateStatus.set('offline');
  updateStatus.set('checking');
  try {
    await registration.update();
    await installed(registration.installing);
  } catch {
    return updateStatus.set(navigator.onLine ? 'unchecked' : 'offline');
  }
  // `onNeedRefresh` has run by now when something new arrived.
  updateStatus.set(pendingUpdate.get() ? 'ready' : 'current');
}

/**
 * The new version takes over and reloads the page once it controls it. A page
 * that was never controlled (the very first visit) gets no such signal, so it
 * reloads anyway after a moment rather than leaving the button spinning.
 */
const RELOAD_FALLBACK_MS = 3000;

/**
 * Registers the service worker in production builds (offline + installable app).
 *
 * A new version never takes over by itself while the app is open: that reload
 * would close the video being edited or stop a recording. It waits, and starts
 * the next time the app is opened, or earlier when the person chooses to (see
 * {@link useUpdate}).
 */
export async function registerServiceWorker(): Promise<void> {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const { registerSW } = await import('virtual:pwa-register');
  const update = registerSW({
    immediate: true,
    onNeedRefresh: () => {
      pendingUpdate.set(async () => {
        await update(true);
        window.setTimeout(() => location.reload(), RELOAD_FALLBACK_MS);
      });
      updateStatus.set('ready');
    },
    onRegisteredSW(_url, registered) {
      if (!registered) return;
      registration = registered;
      if (updateStatus.get() === 'unsupported') updateStatus.set('unchecked');
      const check = () => {
        if (document.visibilityState === 'visible' && navigator.onLine) void registered.update().catch(() => undefined);
      };
      window.setInterval(check, UPDATE_CHECK_MS);
      document.addEventListener('visibilitychange', check);
    },
  });
}

/** Applies a waiting new version (reloads the page), or null while there is none. */
export function useUpdate(): (() => Promise<void>) | null {
  return useSignal(pendingUpdate);
}

export const useUpdateStatus = (): UpdateStatus => useSignal(updateStatus);

/**
 * Installed apps on phones are rarely closed for real, so "the next time the
 * app opens" may be days away. Mounted only while nothing is open, this moves
 * to a waiting version the moment the person leaves the app: the reload happens
 * out of sight and costs nothing.
 */
export function useQuietUpdate(): void {
  const apply = useUpdate();
  useEffect(() => {
    if (!apply) return;
    const onHide = () => {
      if (document.visibilityState === 'hidden') void apply().catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, [apply]);
}

/** The build a device ran last, as `commit@builtAt`. */
const buildKey = `${BUILD.commit}@${BUILD.builtAt}`;

/**
 * True for the first start of a new version on this device (never on a first
 * visit), so the app can say it updated. Read once per page load.
 */
export function startedNewVersion(): boolean {
  if (startedNew === null) {
    const previous = readPreference('last-build');
    if (previous !== buildKey) writePreference('last-build', buildKey);
    startedNew = import.meta.env.PROD && previous !== null && previous !== buildKey;
  }
  return startedNew;
}

let startedNew: boolean | null = null;

const installPrompt = signal<InstallPromptEvent | null>(null);

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Keep the browser's prompt for our own, better placed "Install" button.
    event.preventDefault();
    installPrompt.set(event as InstallPromptEvent);
  });
  window.addEventListener('appinstalled', () => installPrompt.set(null));
}

/** Install support where the browser offers it (Chrome, Edge, Android); null elsewhere. */
export function useInstallPrompt(): (() => Promise<void>) | null {
  const event = useSignal(installPrompt);
  if (!event) return null;
  return async () => {
    await event.prompt();
    await event.userChoice.catch(() => undefined);
    installPrompt.set(null);
  };
}
